/** ADM-001: results for the query in the header's search box, by kind of record. */
import * as stylex from "@stylexjs/stylex";
import type { AdminOrganizationRow, AdminPatientRow, AdminSearchResult, AdminUserRow } from "@lattice/core";
import { ROUTES } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import { api } from "./api.ts";
import { Person, RecordLink, Truncate } from "./bits.tsx";
import type { Column } from "./layout.tsx";
import { FilterChip, FilterRow, PageHead, Table } from "./layout.tsx";
import { useLoad } from "./load.ts";
import type { SearchView } from "./router.ts";
import { href, rememberList, rememberSearch } from "./router.ts";
import { ErrorText, Status } from "./ui.tsx";
import { day, refusal, STAFF_ROLE } from "./words.ts";
const USER_COLUMNS: readonly Column<AdminUserRow>[] = [{
  label: "Name",
  cell: u => <Person name={u.anonymous ? "Anonymous visitor" : u.name} email={u.anonymous ? null : u.email} to={href.user(u.id)} />
}, {
  label: "Email",
  cell: u => u.anonymous ? <span className="x1jty25b">No email yet</span> : <Truncate value={u.email} />
}, {
  label: "Status",
  nowrap: true,
  cell: u => u.locked ? <Status tone="danger">Locked</Status> : <Status tone="ok">Active</Status>
}, {
  label: "Role",
  nowrap: true,
  cell: u => u.staffRole === null ? "Customer" : STAFF_ROLE[u.staffRole]
}, {
  label: "Created",
  numeric: true,
  phoneLabel: true,
  cell: u => day(u.createdAt)
}];
function patientName(p: AdminPatientRow): string {
  const name = [p.firstName, p.lastName].filter(part => part !== null).join(" ");
  return name === "" ? "Name not given" : name;
}
const PATIENT_COLUMNS: readonly Column<AdminPatientRow>[] = [{
  label: "Patient record",
  cell: p => <RecordLink to={href.patient(p.id)} relation="Patient" label={patientName(p)} />
}, {
  label: "Account",
  phoneLabel: true,
  cell: p => p.userId === null || p.email === null ? <span className="x1jty25b">Not claimed</span> : <RecordLink to={href.user(p.userId)} relation="Account" label={p.email} />
}, {
  label: "Created",
  numeric: true,
  phoneLabel: true,
  cell: p => day(p.createdAt)
}];
const CLINIC_COLUMNS: readonly Column<AdminOrganizationRow>[] = [{
  label: "Clinic",
  cell: o => <RecordLink to={href.organization(o.id)} relation="Clinic" label={o.name} />
}, {
  label: "Slug",
  phoneLabel: true,
  cell: o => o.slug ?? "None"
}, {
  label: "Members",
  numeric: true,
  phoneLabel: true,
  cell: o => o.members
}, {
  label: "Created",
  numeric: true,
  phoneLabel: true,
  cell: o => day(o.createdAt)
}];
function emptyFor(view: SearchView, query: string, none: boolean): string {
  if (none) return `Nothing matches “${query}”. Try part of a name or email.`;
  if (view === "patients") return `No patient records match “${query}”. Other collections might.`;
  if (view === "clinics") return `No clinics match “${query}”. Other collections might.`;
  return `No accounts match “${query}”. Other collections might.`;
}
function Results({
  result,
  view
}: {
  readonly result: AdminSearchResult;
  readonly view: SearchView;
}) {
  const none = result.users.length + result.patients.length + result.organizations.length === 0;
  const rows = view === "patients" ? result.patients.length : view === "clinics" ? result.organizations.length : result.users.length;
  return <>
      <PageHead title={`Results for “${result.query}”`} meta={none ? "No matches" : `${rows} in this collection`} />
      <FilterRow>
        <FilterChip to={href.search(result.query, "accounts")} label="Accounts" count={result.users.length} current={view === "accounts"} />
        <FilterChip to={href.search(result.query, "patients")} label="Patient records" count={result.patients.length} current={view === "patients"} />
        <FilterChip to={href.search(result.query, "clinics")} label="Clinics" count={result.organizations.length} current={view === "clinics"} />
      </FilterRow>
      {view === "patients" ? <Table label="Patient records" columns={PATIENT_COLUMNS} rows={result.patients} rowKey={p => p.id} empty={emptyFor(view, result.query, none)} /> : null}
      {view === "clinics" ? <Table label="Clinics" columns={CLINIC_COLUMNS} rows={result.organizations} rowKey={o => o.id} empty={emptyFor(view, result.query, none)} /> : null}
      {view === "accounts" ? <Table label="Accounts" columns={USER_COLUMNS} rows={result.users} rowKey={u => u.id} empty={emptyFor(view, result.query, none)} /> : null}
    </>;
}
function Found({
  query,
  view
}: {
  readonly query: string;
  readonly view: SearchView;
}) {
  rememberSearch(query);
  rememberList(href.search(query, view));
  const [found] = useLoad(`search-${query}`, () => api<AdminSearchResult>(ROUTES.adminSearch, {
    query: {
      q: query
    }
  }));
  if (found.state === "loading") return <PageHead title={`Results for “${query}”`} meta="Searching" />;
  if (found.state === "failed") return <ErrorText>{refusal(found.error)}</ErrorText>;
  return <Results result={found.data} view={view} />;
}
export function Search({
  query,
  view
}: {
  readonly query: string;
  readonly view: SearchView;
}) {
  return <Found query={query.trim()} view={view} />;
}